import React, { useCallback } from 'react';

import ResponsiveImage, { ResponsiveImageProps } from 'ui/Image';
import css from './SupplierCard.module.scss';
import { faStar } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { FireIcon, NewIcon, WedPointsIcon } from '@assets/icons';
import { VwpPackage } from '../types';
import { useListenKey } from '@hooks/useListenKey';
import useDraggableClickHandler from '@hooks/useDraggableClick';

function CardPromo({ points }: { points?: number }){
    return <div className={css.card__promo}>
        <WedPointsIcon className={css.card__promoIcon} />
        <div>
            Book & Earn <strong>{points}+ WedPoints</strong>
        </div>
    </div>
}

const CardMedia = function({
    children,
    src,
    alt,
    ...rest
}: {
    children?: React.ReactNode
} & Omit<ResponsiveImageProps, 'className'>){
    return <div className={css.card__media}>
        {children}
        <ResponsiveImage src={src} alt={alt} className={css.card__media__image} {...rest}/>
    </div>
}

CardMedia.Badge = function CardImageBadge({ children }: { children: React.ReactNode}){
    return <div className={css.card__media__badge}>{children}</div>
}

CardMedia.BadgeBestSeller = function CardImageBadge(){
    return (<CardMedia.Badge>
        <FireIcon />
        Best seller
    </CardMedia.Badge>)
}

CardMedia.BadgeNew = function CardImageBadge(){
    return <div className={[css.card__media__badge, css['card__media__badge--new']].join(' ')}>
        <NewIcon />
        New
    </div>
}

function CardDescription({
    header,
    title,
    description
}: {
    header?: React.ReactNode,
    title: string,
    description: React.ReactNode,
}){
    return (<div className={css.supplier_description}>
        {header && <div className={css.supplier_description__header}>
            {header}
        </div>}
        <h3 className={css.card__title}>{title}</h3>
        <div className={css.card__text}>
            {description}
        </div>
    </div>)
}

const SupplierRating = function({ rating }: { rating: string }){
    return (<div className={css.rating}>
        <Icon icon={faStar} className={css.ratingIcon} style={{maxWidth: '2rem', maxHeight: '2rem'}}/>
        {rating}
    </div>);
}
const SupplierPrice = function({ price }: { price: number} ){
    return (<div className={css.price}>
        <span className={css.price__label}>from</span>
        <span className={css.price__sign}>$</span>
        <span className={css.price__value}>{(new Intl.NumberFormat()).format(price)}</span>
    </div>);
}

type SupplierCardProps = {
    onClick?: (supplierId: number) => void,
    supplier: VwpPackage & {
        image: string,
        isNew?: boolean,
        isBestSeller?: boolean,
    }
};

const CARD_SIZE_W = 279;
const CARD_SIZE_H = 176;

const IMAGE_SOURCES = [
    {
        media: '',
        width: CARD_SIZE_W,
        height: CARD_SIZE_H,
    },
];

function SupplierCard({ onClick, supplier }: SupplierCardProps){
    const handleClick = useCallback(() => {
        onClick && onClick(supplier.Id);
    }, [supplier.Id, onClick]);

    const ref = React.useRef<HTMLDivElement>(null);
    useListenKey(ref, handleClick, "Enter");
    const clickHandlers = useDraggableClickHandler(handleClick);

    return <div className={css.card} tabIndex={0} ref={ref}>
        {supplier.WedPointsToEarn && <CardPromo points={supplier.WedPointsToEarn}/>}
        <CardMedia
            src={supplier.image}
            alt={supplier.SupplierName}
            sources={IMAGE_SOURCES}
            onDragStart={(e) => e.preventDefault()}
            width={CARD_SIZE_W}
            height={CARD_SIZE_H}
            {...clickHandlers}
            >
            {supplier.isNew && <CardMedia.BadgeNew />}
            {supplier.isBestSeller && <CardMedia.BadgeBestSeller />}
        </CardMedia>

        <CardDescription 
            header={<>
                <SupplierRating rating={supplier.SupplierReviewRating} />
                <SupplierPrice price={supplier.RrpPrice}/>
            </>}
            title={supplier.SupplierName}
            description={<div dangerouslySetInnerHTML={{ __html: supplier.Inclusions}}></div>}
        />

        <div className={css.card__footer} onClick={handleClick}>
            {typeof onClick === 'function'
                ? <span className={css.card__button}>View Packages &rarr;</span>
                : <a href="#" className={css.card__button}>View Packages &rarr;</a>
            }
        </div>
    </div>
}

export default SupplierCard;